import { useEffect, useCallback, useRef } from 'react';
import { 
  clearHintLetter,
  saveHintLetter,
  loadHintLetterDate,
  GameMode,
  GameStatus
} from '@/utils/gameState';
import { 
  getHintLetter,
} from '@/utils/wordUtils';
import { getDateString, getNextDaysDate, getNormalizedDate } from '@/utils/dateUtils';
import ResultModal from '@/components/ResultModal';
import Navbar from '@/components/Navbar';
import GameArea from '@/components/GameArea';
import Keyboard from '@/components/Keyboard';
import HelpInfoModal from '@/components/HelpInfoModal';
import { useGame } from '@/context/GameContext';
import ArchiveModal from '@/components/ArchiveModal';
import { getRandomArchiveDate } from '@/utils/gameUtils';
import { toast } from '@/hooks/use-toast';
import {
  buildLevelName,
  buildPlayedDate,
  buildPostScoreLevel,
  buildPuzzleDate,
  buildPuzzleId,
  buildScore,
  ModeSelectionSource,
  trackEvent
} from '@/utils/analytics';

const Index = () => {
  const {
    gameState,
    currentGuess,
    gameConfig,
    setGameConfig,
    gameCompletions,
    showResult,
    setShowResult,
    showHelpInfo,
    setShowHelpInfo,
    showArchive,
    setShowArchive,
    revealAnimationComplete,
    canPlayEncore,
    timerActive,
    hintLetter,
    setHintLetter,
    initializeGame
  } = useGame();

  const previousGameSnapshotRef = useRef<{ gameId: string; status: GameStatus } | null>(null);
  const targetWordDateKey = gameState.targetWordDate ? getDateString(gameState.targetWordDate) : 'no-date';
  const targetWordSalt = gameState.targetWord.salt || 'no-salt';
  const activeGameId = `${gameConfig.mode}:${targetWordDateKey}:${targetWordSalt}`;
  const todayString = getDateString(getNormalizedDate());
  const activePuzzleDate = gameState.targetWordDate ?? gameConfig.date;
  const isActivePuzzleToday = activePuzzleDate ? getDateString(activePuzzleDate) === todayString : false;
  const canPlayEncoreForResult = canPlayEncore && gameConfig.mode === GameMode.DAILY && isActivePuzzleToday;
  
  // Hide result modal on game mode change
  useEffect(() => {
    setShowResult(false);
  }, [gameConfig.mode, setShowResult]);
  
  // Handles selecting a game mode
  const handleSelectGameMode = useCallback((newMode: GameMode, source: ModeSelectionSource) => {
    trackEvent('mode_selected', {
      from_mode: gameConfig.mode,
      to_mode: newMode,
      source
    });

    const newDate = newMode === GameMode.DAILY ? getNormalizedDate() : null;
    setGameConfig({ mode: newMode, date: newDate });
  }, [gameConfig.mode, setGameConfig]);

  const handleArchiveSelect = useCallback((date: Date) => {
    const normalizedDate = getNormalizedDate(date);

    trackEvent('archive_selected', {
      selection_type: 'date',
      puzzle_date: getDateString(normalizedDate)
    });

    if (gameConfig.mode !== GameMode.ARCHIVE) {
      trackEvent('mode_selected', {
        from_mode: gameConfig.mode,
        to_mode: GameMode.ARCHIVE,
        source: 'mode_dropdown'
      });
    }

    // Use ARCHIVE mode with the selected date
    setGameConfig({ mode: GameMode.ARCHIVE, date: normalizedDate });
  }, [gameConfig.mode, setGameConfig]);

  const handleRandomArchiveSelect = useCallback((source: ModeSelectionSource = 'mode_dropdown') => {
    const randomDate = getRandomArchiveDate(gameCompletions);

    if (randomDate) {
      const normalizedDate = getNormalizedDate(randomDate);

      trackEvent('archive_selected', {
        selection_type: 'random',
        puzzle_date: getDateString(normalizedDate)
      });

      if (gameConfig.mode !== GameMode.ARCHIVE) {
        trackEvent('mode_selected', {
          from_mode: gameConfig.mode,
          to_mode: GameMode.ARCHIVE,
          source
        });
      }

      // Use ARCHIVE mode with the random date
      setGameConfig({ mode: GameMode.ARCHIVE, date: normalizedDate });
    } else {
      if (gameConfig.mode !== GameMode.RANDOM) {
        trackEvent('mode_selected', {
          from_mode: gameConfig.mode,
          to_mode: GameMode.RANDOM,
          source
        });
      }

      setGameConfig({ mode: GameMode.RANDOM, date: null });

      toast({
        title: "No archives left to play!",
        description: "Playing a random word instead.",
        duration: 5000
      });
    }
  }, [gameCompletions, gameConfig.mode, setGameConfig]);
  
  // What to do when the game ends
  useEffect(() => {
    const previousSnapshot = previousGameSnapshotRef.current;
    const hasCompletedGame = gameState.gameStatus !== GameStatus.IN_PROGRESS;
    const isFreshCompletion = Boolean(
      previousSnapshot &&
      previousSnapshot.gameId === activeGameId &&
      previousSnapshot.status === GameStatus.IN_PROGRESS &&
      hasCompletedGame
    );

    if (isFreshCompletion) {
      const attemptCount = gameState.guesses.length;
      const success = gameState.gameStatus === GameStatus.WIN;
      const puzzleDate = buildPuzzleDate(gameConfig.mode, gameState.targetWordDate ?? gameConfig.date);
      const puzzleId = buildPuzzleId(gameConfig.mode, gameState.targetWordDate ?? gameConfig.date);

      trackEvent('level_end', {
        level_name: buildLevelName(gameConfig.mode, gameState.targetWordDate ?? gameConfig.date),
        puzzle_id: puzzleId,
        game_mode: gameConfig.mode,
        success,
        attempt_count: attemptCount,
        played_date: buildPlayedDate(),
        puzzle_date: puzzleDate
      });

      trackEvent('post_score', {
        score: buildScore(attemptCount, success),
        attempt_count: attemptCount,
        level: buildPostScoreLevel(gameConfig.mode, gameState.targetWordNumber),
        puzzle_id: puzzleId,
        game_mode: gameConfig.mode,
        played_date: buildPlayedDate(),
        puzzle_date: puzzleDate
      });

      if (gameConfig.mode === GameMode.DAILY) {
        const hintLetterDate = loadHintLetterDate();
        const solvedPuzzleDate = getDateString(gameState.targetWordDate ?? gameConfig.date ?? getNormalizedDate());

        if (hintLetterDate === solvedPuzzleDate) {
          setHintLetter(null);
          clearHintLetter();
        }
      } else if (gameConfig.mode === GameMode.ENCORE && gameState.gameStatus === GameStatus.WIN) {
        const tomorrow = getNextDaysDate();
        const newHintLetter = getHintLetter(tomorrow);

        setHintLetter(newHintLetter);
        saveHintLetter(newHintLetter, tomorrow);
      }

      setShowResult(true);
    }

    previousGameSnapshotRef.current = {
      gameId: activeGameId,
      status: gameState.gameStatus
    };
  }, [activeGameId, gameState.gameStatus, gameState.guesses.length, gameState.targetWordDate, gameState.targetWordNumber, gameConfig.mode, gameConfig.date, setHintLetter, setShowResult]);
  
  // Handle starting a new game
  const handleNewGame = useCallback((newMode: GameMode, newDate?: Date, source: ModeSelectionSource = 'result_modal') => {
    let date = newDate;
    
    if (newMode === GameMode.DAILY) {
      // For DAILY mode, use today's date
      date = getNormalizedDate();
    }

    // For past dates, use handleRandomArchiveSelect to find a random unplayed date
    if (newMode === GameMode.ARCHIVE && !newDate) {
      handleRandomArchiveSelect(source);
    } else {
      const newConfig = {
        mode: newMode,
        date
      };
      
      setGameConfig(newConfig);
      initializeGame(newConfig);
    }
  }, [initializeGame, setGameConfig, handleRandomArchiveSelect]);
  
  return (
    <div className="min-h-[100svh] flex flex-col bg-background">
      <div>
        <Navbar
          onHelpClick={() => setShowHelpInfo(true)}
          gameMode={gameConfig.mode}
          onSelectGameMode={(mode) => handleSelectGameMode(mode, 'mode_dropdown')}
          canPlayEncore={canPlayEncore}
        />
      </div>
      
      <main className="flex-1 flex flex-col pb-safe-or-[theme(spacing.1)] px-2 max-w-full mx-auto w-full justify-between">
        <GameArea 
          guesses={gameState.guesses}
          evaluations={gameState.evaluations}
          currentGuess={currentGuess}
          revealed={revealAnimationComplete}
          gameMode={gameConfig.mode}
          timerActive={timerActive}
          currentRowIndex={gameState.currentRowIndex}
        />
        
        <div className="mt-auto pb-2 sticky bottom-0 bg-background">
          <Keyboard
            hintLetter={hintLetter}
            keyboardState={gameState.keyboardState}
            gameMode={gameConfig.mode}
          />
        </div>
      </main>
      
      <ResultModal 
        gameState={gameState} 
        isOpen={showResult} 
        onClose={() => setShowResult(false)}
        onNewGame={handleNewGame}
        gameMode={gameConfig.mode}
        onPlayEncore={() => handleSelectGameMode(GameMode.ENCORE, 'result_modal')}
        canPlayEncore={canPlayEncoreForResult}
        hintLetter={hintLetter}
      />

      <HelpInfoModal
        isOpen={showHelpInfo}
        onClose={() => setShowHelpInfo(false)}
      />

      <ArchiveModal 
        isOpen={showArchive}
        onClose={() => setShowArchive(false)}
        onDateSelect={handleArchiveSelect}
        onRandomSelect={() => handleRandomArchiveSelect('mode_dropdown')}
      />
    </div>
  );
};

export default Index;
